import { DateTime } from 'luxon'

export type TGoalStatus = 'on_track' | 'behind' | 'off_track' | 'paused'
export type TGoalProgressMode = 'cadence' | 'source' | 'manual'
export type TGoalType = 'consistency' | 'time'
export type TGoalWeekActivity = 'complete' | 'partial' | 'none'

export interface TGoalPhase {
    name: string
    cadenceLabel: string
    weeklyHours: number
    dateSpanLabel: string
    weeks: number
}

/** An advisory row accepted with the plan; it never creates work or contributes progress. */
export interface TGoalPlanCadence {
    title: string
    frequencyLabel: string
}

/**
 * A saved contribution: a completed one-off task link, a completed recurring-template task hook,
 * or a manual log. Entries saved before contributions were timestamped only carry `date` and never
 * count toward a week.
 */
export interface TGoalContribution {
    id?: string
    title: string
    date?: string
    source: string
    taskId?: string
    at?: string
    amount?: number
    note?: string
}

/**
 * What put one filled unit of the meter there. The server already distinguishes the three, so this
 * reads its `source` rather than inferring: a recurring-template hook saves 'Recurring', a one-off
 * task link 'Task', and a by-hand entry 'Logged'. Legacy rows carry neither a source we know nor a
 * task, and read as by hand.
 */
export type TGoalUnitSource = 'task' | 'recurring' | 'manual'

export const goalUnitSource = (contribution: TGoalContribution): TGoalUnitSource => {
    if (contribution.source === 'Recurring') return 'recurring'
    if (contribution.source === 'Task' || contribution.taskId) return 'task'
    return 'manual'
}

export type TGoalReviewRead = 'moving' | 'stuck' | 'not_now' | 'done'
export type TGoalReviewFix =
    | 'ease'
    | 'extend'
    | 'keep'
    | 'rethink'
    | 'pause'
    | 'archive'
    /** The old "Archive as done". Still read on past weeks; the review now answers 'achieve'. */
    | 'archive_done'
    | 'achieve'
    | 'new_target'

/**
 * One goal's answer in one weekly review: the owner's own read, the one change made with it, and
 * the one step picked. It is stored beside the server's `status` and never replaces it — see
 * goalReviewRules.ts for everything derived from it.
 */
export interface TGoalReviewEntry {
    goalId: string
    read?: TGoalReviewRead
    fix?: TGoalReviewFix
    stepTaskId?: string
    /** The step's title when it was picked, so the summary reads even after the task changes. */
    stepTitle?: string
    /** yyyy-MM-dd */
    stepDay?: string
    skipped?: boolean
    at: string
    /** The Sunday the review week started on; present on entries joined onto a goal. */
    weekStart?: string
}

export interface TGoal {
    id: string
    /** Present only while create is waiting for the server's authoritative id and fields. */
    optimisticId?: string
    title: string
    why: string
    timeframeLabel: string
    targetLabel: string
    paceLabel: string
    /** Derived by the server for paced goals, like status, weekLabel, weeks, contractLine and thisWeek. */
    progress: number
    progressLabel: string
    status: TGoalStatus
    progressMode: TGoalProgressMode
    goalType?: TGoalType
    phases?: TGoalPhase[]
    planCadences?: TGoalPlanCadence[]
    contractLine?: string
    weekLabel?: string
    startDate?: string
    asOf?: string
    weeks?: TGoalWeekActivity[]
    recent: TGoalContribution[]
    footnote?: string
    /** yyyy-MM-dd; absent = open-ended. */
    endDate?: string
    /** Sessions (or hours when unit is 'hrs') per week; absent = no weekly pace. */
    weeklyTarget?: number
    unit?: 'hrs'
    /** User chose not to see the met-week step-up offer for this goal. */
    rampStepUpDisabled?: boolean
    thisWeek?: number
    /**
     * The contributions the server counted toward this week, oldest first — one per filled unit of
     * the meter. Derived by recomputeGoal alongside `thisWeek`, so the labels and the count can
     * never disagree; the client must not re-derive it from `recent`.
     */
    thisWeekUnits?: TGoalContribution[]
    /**
     * ISO timestamp, absent when the goal is active. Archived goals still arrive in the list so
     * "Show archived" and Unarchive need no second request, but every surface hides them by default
     * — see isGoalArchived/activeGoals below, which own that rule.
     */
    archivedAt?: string
    /** 'done' when the weekly review closed the goal as finished; absent for a plain archive. */
    archivedReason?: 'done'
    /**
     * ISO timestamp, absent unless the owner marked the goal achieved. Unlike archiving it keeps the
     * goal's history (recent, completed-task links, reviews); like archiving it freezes the goal.
     * See isGoalAchieved/achievedGoals below.
     */
    achievedAt?: string
    /**
     * The owner's weekly reads of this goal, newest first, joined on by the goals list. Kept on
     * achieved goals; absent on archived goals and on every single-goal response, so the cache must not copy it from those.
     */
    reviews?: TGoalReviewEntry[]
    created_at?: string
    updated_at?: string
}

/** The fields a client may change; everything derived is recomputed by the server. */
export type TGoalPatch = Partial<
    Pick<
        TGoal,
        | 'title'
        | 'why'
        | 'timeframeLabel'
        | 'targetLabel'
        | 'paceLabel'
        | 'progressMode'
        | 'goalType'
        | 'phases'
        | 'planCadences'
        | 'status'
        | 'startDate'
        | 'endDate'
        | 'weeklyTarget'
        | 'rampStepUpDisabled'
    >
> & {
    /** Empty string clears an hours-based plan back to session/manual units. */
    unit?: TGoal['unit'] | ''
    /** Repair's "Keep this version": earlier quiet weeks stop counting. */
    forgive?: boolean
}

/**
 * GTDatePicker reports a picked day as a full ISO timestamp and "no date" as the epoch, neither of
 * which the goal API takes: it wants bare yyyy-MM-dd and reads '' as open-ended. modifyTask does
 * the same reformat for task due dates; this lives with the patch contract so every caller is
 * converted on the way out rather than each picker having to remember.
 */
export const normalizeGoalEndDate = (endDate: string): string => {
    const picked = DateTime.fromISO(endDate)
    if (!picked.isValid) return ''
    // The picker's "clear" value, built the same way and in the same zone as GTDatePicker builds it.
    if (picked.hasSame(DateTime.fromMillis(0), 'day')) return ''
    return picked.toFormat('yyyy-MM-dd')
}

/**
 * Pausing and resuming are the only status changes the server takes from the client for a paced
 * goal — every other status is derived from its contributions (backend/api/goals.go, GoalModify).
 * Both patches live here so the property chip, the paused banner and Repair send the same thing:
 * resuming forgives the quiet weeks, or the goal would come back already off track.
 */
export const GOAL_PAUSE_PATCH: TGoalPatch = { status: 'paused' }
export const GOAL_RESUME_PATCH: TGoalPatch = { status: 'on_track', forgive: true }

/**
 * Archiving is a separate axis from status: an archived goal keeps the verdict it had, so restoring
 * it reads the way it did before. These two are the only place that rule is written down — the list,
 * the selector and the details pane all ask here rather than testing `archivedAt` themselves.
 */
export const isGoalArchived = (goal: TGoal): boolean => !!goal.archivedAt
/**
 * Achieved is the third axis: a finished goal that keeps its history. Archive wins if both are ever
 * set (the server clears achievedAt on archive, but an old cache could briefly hold both).
 */
export const isGoalAchieved = (goal: TGoal): boolean => !!goal.achievedAt && !isGoalArchived(goal)
/** Archived or achieved: takes no new work, links, logs or edits. Not a history rule — see isGoalAchieved. */
export const isGoalClosed = (goal: TGoal): boolean => isGoalArchived(goal) || isGoalAchieved(goal)
export const activeGoals = (goals: TGoal[]): TGoal[] => goals.filter((goal) => !isGoalClosed(goal))
/**
 * A goal the weekly review archived "as done" before achieving existed. It reads as accomplished,
 * but its history was discarded by that archive, so its card cannot show a final total.
 */
export const isLegacyFinishedGoal = (goal: TGoal): boolean => isGoalArchived(goal) && goal.archivedReason === 'done'
/** Archived and not finished: what "Show archived" reveals. Legacy finished goals live in Accomplished. */
export const archivedGoals = (goals: TGoal[]): TGoal[] =>
    goals.filter((goal) => isGoalArchived(goal) && !isLegacyFinishedGoal(goal))
const finishedAt = (goal: TGoal): string => goal.achievedAt ?? goal.archivedAt ?? ''
/** The Accomplished section: achieved goals plus legacy finished ones, most recent first. */
export const accomplishedGoals = (goals: TGoal[]): TGoal[] =>
    goals
        .filter((goal) => isGoalAchieved(goal) || isLegacyFinishedGoal(goal))
        .sort((a, b) => finishedAt(b).localeCompare(finishedAt(a)))

/**
 * The accomplished card's readout, in the slot the weekly counter uses on an active card: what the
 * goal added up to and how long it ran. "12 sessions total · 8 weeks", "36 hrs total · 3 weeks".
 * A self-graded goal with nothing counted reads as its duration alone.
 */
export const accomplishedReadout = (goal: TGoal): string => {
    if (isLegacyFinishedGoal(goal)) return 'Progress history unavailable'
    const total = goal.recent.reduce((sum, contribution) => sum + (contribution.amount ?? 0), 0)
    const start = DateTime.fromISO(goal.startDate || goal.created_at || '')
    const end = DateTime.fromISO(goal.achievedAt ?? '')
    const days = start.isValid && end.isValid ? Math.max(0, end.diff(start, 'days').days) : null
    const weeks = days === null ? null : Math.max(1, Math.round(days / 7))
    const duration = weeks === null ? null : `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`
    const unit = goal.unit === 'hrs' ? 'hrs' : total === 1 ? 'session' : 'sessions'
    const amount = total > 0 ? `${formatGoalAmount(total)} ${unit} total` : null
    return [amount, duration].filter(Boolean).join(' · ') || 'Achieved'
}

/** "Achieved Oct 23", or "Finished Oct 3" for a legacy goal whose only date is its archive. */
export const accomplishedDateLabel = (goal: TGoal): string => {
    const at = DateTime.fromISO(finishedAt(goal))
    if (!at.isValid) return isGoalAchieved(goal) ? 'Achieved' : 'Finished'
    return `${isGoalAchieved(goal) ? 'Achieved' : 'Finished'} ${at.toFormat('LLL d')}`
}

/**
 * What happens to a goal's linked tasks when it is archived. There is no default: keeping the tasks
 * and removing them are different outcomes, so the dialog makes the user pick and the server refuses
 * a request that does not say (backend/api/goal_archive.go).
 */
export type TGoalTaskHandling = 'keep' | 'archive'

export const goalStepUpTarget = (goal: Pick<TGoal, 'weeklyTarget' | 'unit'>): number =>
    (goal.weeklyTarget ?? 0) + (goal.unit === 'hrs' ? 0.5 : 1)

export const goalStepUpPatch = (goal: Pick<TGoal, 'weeklyTarget' | 'unit'>): TGoalPatch => ({
    weeklyTarget: goalStepUpTarget(goal),
    forgive: true,
})

export const shouldOfferGoalStepUp = (
    goal: Pick<TGoal, 'weeklyTarget' | 'progressMode' | 'status' | 'weeks' | 'rampStepUpDisabled'>
): boolean => {
    if (!goal.weeklyTarget || goal.progressMode === 'manual' || goal.status !== 'on_track' || goal.rampStepUpDisabled) {
        return false
    }
    const recentWeeks = goal.weeks?.slice(-2) ?? []
    return recentWeeks.length === 2 && recentWeeks.every((week) => week === 'complete')
}

/** The verdicts a self-graded goal's owner may set; pausing is separate and open to any goal. */
export const GOAL_SELF_GRADES: TGoalStatus[] = ['on_track', 'behind', 'off_track']

export const GOAL_STATUS_LABEL: Record<TGoalStatus, string> = {
    on_track: 'On track',
    behind: 'Behind',
    off_track: 'Off track',
    paused: 'Paused',
}

/** One decimal at most, matching formatGoalAmount in backend/api/goal_progress.go. */
export const formatGoalAmount = (n: number): string => String(Math.round(n * 10) / 10)

/**
 * The muted tail beside the count: "2 from tasks", "2 by hand", "2 from tasks, 1 by hand". The
 * glyphs on the meter say the same thing visually; this is the text channel for the same fact, so
 * the attribution never depends on being able to see a 9px mark.
 *
 * It therefore has to measure what the visual channel measures. A session goal draws one segment
 * per entry, so it counts entries; an hours goal draws a bar split by hours, so it sums hours —
 * counting entries there would put "1 from task" beside a bar that is two hours long.
 */
export const goalAttributionTail = (units: TGoalContribution[], unit?: TGoal['unit']): string => {
    const isHours = unit === 'hrs'
    const measure = (of: TGoalContribution[]) => (isHours ? of.reduce((sum, u) => sum + (u.amount ?? 0), 0) : of.length)
    const fromTasks = measure(units.filter((u) => goalUnitSource(u) !== 'manual'))
    const byHand = measure(units.filter((u) => goalUnitSource(u) === 'manual'))
    const label = (amount: number, suffix: string) =>
        isHours
            ? `${formatGoalAmount(amount)} hrs ${suffix}`
            : `${amount} ${suffix === 'from tasks' && amount === 1 ? 'from task' : suffix}`
    const parts: string[] = []
    if (fromTasks > 0) parts.push(label(fromTasks, 'from tasks'))
    if (byHand > 0) parts.push(label(byHand, 'by hand'))
    return parts.join(', ')
}

/**
 * The one hint the block may show, in priority order. Everything else — a healthy week, a met week —
 * gets no words at all, which is the point: the meter is the explanation and prose is the exception.
 *
 * `status` stands in for "was last week quiet": the server already decided that in recomputeGoal
 * (weighing the grace window and Repair's forgiveness), and re-deriving it from `weeks` here would
 * be a second, worse copy of that judgement.
 */
export type TGoalHint = { kind: 'nothing-linked' } | { kind: 'quiet' } | { kind: 'all-by-hand' } | null

export const goalHint = ({
    units,
    hasFeed,
    hasHistory,
    status,
}: {
    units: TGoalContribution[]
    /** Anything that moves the goal on its own: a linked task, or a hooked recurring template. */
    hasFeed: boolean
    hasHistory: boolean
    status: TGoalStatus
}): TGoalHint => {
    if (status === 'paused') return null
    if (!hasHistory && !hasFeed) return { kind: 'nothing-linked' }
    // off_track already has its own Repair banner above the block; saying it twice is noise.
    if (status === 'behind') return { kind: 'quiet' }
    if (units.length >= 2 && units.every((u) => goalUnitSource(u) === 'manual')) return { kind: 'all-by-hand' }
    return null
}
